import { create } from 'zustand';
import {
  getAudiences,
  getNotificationHistory,
  createCampaign,
  updateCampaign,
  sendCampaign,
  deleteCampaign,
  getUsersByRole,
  Audience,
  AudienceUser,
  NotificationRecord,
  CampaignData,
} from '@/services/PagesServices/SuperAdminMarketingService';

interface MarketingAndNotificationState {
  audiences: Audience[] | null;
  audienceUsers: AudienceUser[] | null;
  notificationHistory: NotificationRecord[] | null;
  loading: boolean;
  sending: boolean;
  error: string | null;

  fetchAudiences: () => Promise<void>;
  fetchAudienceUsers: () => Promise<void>;
  fetchNotificationHistory: () => Promise<void>;
  createAndSendCampaign: (data: CampaignData, sendImmediately: boolean) => Promise<{ success: boolean; error?: string }>;
  updateAndSendCampaign: (uuid: string, data: CampaignData, sendImmediately: boolean) => Promise<{ success: boolean; error?: string }>;
  resendCampaign: (uuid: string) => Promise<{ success: boolean; error?: string }>;
  duplicateCampaign: (record: NotificationRecord) => Promise<{ success: boolean; error?: string }>;
  cancelCampaign: (uuid: string) => Promise<{ success: boolean; error?: string }>;
  resetError: () => void;
}

// Audience display names map back to role IDs (inverse of getNotificationHistory).
const ROLE_BY_NAME: Record<string, number> = { Trainees: 1, Instructors: 2, Admins: 3 };

const useSuperAdminMarketingStore = create<MarketingAndNotificationState>((set, get) => ({
  audiences: null,
  audienceUsers: null,
  notificationHistory: null,
  loading: false,
  sending: false,
  error: null,

  fetchAudiences: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getAudiences();
      set({ audiences: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  fetchAudienceUsers: async () => {
    try {
      const response = await getUsersByRole();
      set({ audienceUsers: response.data || [] });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  fetchNotificationHistory: async () => {
    try {
      set({ loading: true, error: null });
      const response = await getNotificationHistory();
      set({ notificationHistory: response.data || [], loading: false });
    } catch (error: any) {
      set({ error: error.message, loading: false });
      throw error;
    }
  },

  createAndSendCampaign: async (data: CampaignData, sendImmediately: boolean) => {
    try {
      set({ sending: true, error: null });

      // First create the campaign
      const createResponse = await createCampaign(data);

      if (!createResponse.success) {
        set({ sending: false, error: createResponse.error?.message || 'Failed to create campaign' });
        return { success: false, error: createResponse.error?.message };
      }

      // If sendImmediately is true and campaign was created, send it
      if (sendImmediately && createResponse.data?.uuid) {
        const sendResponse = await sendCampaign(createResponse.data.uuid);

        if (!sendResponse.success) {
          set({ sending: false, error: sendResponse.error?.message || 'Campaign created but failed to send' });
          return { success: false, error: sendResponse.error?.message };
        }
      }

      set({ sending: false });

      // Refresh history after successful send
      get().fetchNotificationHistory();

      return { success: true };
    } catch (error: any) {
      set({ sending: false, error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Update an existing (draft/scheduled) campaign, optionally sending it now.
  updateAndSendCampaign: async (uuid: string, data: CampaignData, sendImmediately: boolean) => {
    if (!uuid) return { success: false, error: 'Missing campaign id to update' };
    try {
      set({ sending: true, error: null });

      const updateResponse = await updateCampaign(uuid, data);
      if (!updateResponse.success) {
        set({ sending: false, error: updateResponse.error?.message || 'Failed to update campaign' });
        return { success: false, error: updateResponse.error?.message };
      }

      if (sendImmediately) {
        const sendResponse = await sendCampaign(uuid);
        if (!sendResponse.success) {
          set({ sending: false, error: sendResponse.error?.message || 'Campaign updated but failed to send' });
          return { success: false, error: sendResponse.error?.message };
        }
      }

      set({ sending: false });
      get().fetchNotificationHistory();
      return { success: true };
    } catch (error: any) {
      set({ sending: false, error: error.message });
      return { success: false, error: error.message };
    }
  },

  // Re-send an existing campaign to its audience.
  resendCampaign: async (uuid: string) => {
    if (!uuid) return { success: false, error: 'This notification has no id to resend' };
    try {
      set({ sending: true, error: null });
      const res = await sendCampaign(uuid);
      set({ sending: false });
      if (!res.success) return { success: false, error: res.error?.message || 'Failed to resend' };
      get().fetchNotificationHistory();
      return { success: true };
    } catch (error: any) {
      set({ sending: false });
      return { success: false, error: error?.message || 'Failed to resend' };
    }
  },

  // Create a draft copy of an existing campaign.
  duplicateCampaign: async (record: NotificationRecord) => {
    try {
      set({ sending: true, error: null });
      const roles = (record.audiences || [])
        .map(name => ROLE_BY_NAME[name])
        .filter((r): r is number => r !== undefined);
      const data: CampaignData = {
        title: `${record.title} (Copy)`,
        subject: record.title,
        message: record.message,
        targetAudience: { roles },
        deliveryMethod: record.type,
        scheduledFor: null,
      };
      const res = await createCampaign(data);
      set({ sending: false });
      if (!res.success) return { success: false, error: res.error?.message || 'Failed to duplicate' };
      get().fetchNotificationHistory();
      return { success: true };
    } catch (error: any) {
      set({ sending: false });
      return { success: false, error: error?.message || 'Failed to duplicate' };
    }
  },

  // Cancel a notification: soft-deletes it so it's removed and won't be sent.
  cancelCampaign: async (uuid: string) => {
    if (!uuid) return { success: false, error: 'This notification has no id to cancel' };
    try {
      set({ sending: true, error: null });
      const res = await deleteCampaign(uuid);
      set({ sending: false });
      if (!res.success) return { success: false, error: res.error?.message || 'Failed to cancel' };
      get().fetchNotificationHistory();
      return { success: true };
    } catch (error: any) {
      set({ sending: false });
      return { success: false, error: error?.message || 'Failed to cancel' };
    }
  },

  resetError: () => {
    set({ error: null });
  },
}));

export default useSuperAdminMarketingStore;
